import React, { Component, ReactNode } from 'react';
import { AlertTriangle, Home } from 'lucide-react';
import { Navigate } from 'react-router-dom';

interface Props {
  children: ReactNode;
  fallbackPath?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class UsernameErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Username redirect error caught by boundary:', error, errorInfo);

    // Report to error tracking service in production
    if (process.env.NODE_ENV === 'production') {
      this.reportError(error, errorInfo);
    }
  }

  private reportError = (error: Error, errorInfo: React.ErrorInfo) => {
    // This would integrate with your error tracking service (Sentry, etc.)
    console.error('Username error reported to tracking service:', { error, errorInfo });
  };

  private handleReturnHome = () => {
    this.setState({
      hasError: false,
      error: null
    });
  };

  render() {
    if (this.state.hasError) {
      // If there's a fallback path specified, redirect there
      if (this.props.fallbackPath) {
        return <Navigate to={this.props.fallbackPath} replace />;
      }

      // Default error UI with option to go home
      return (
        <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-dark-900 border border-gray-700 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>

            <h2 className="text-xl font-bold text-white mb-2">
              Page Not Found
            </h2>

            <p className="text-gray-400 mb-6">
              We couldn't find the creator profile you're looking for. The username might not exist or there was an error loading the page.
            </p>

            <div className="space-y-3">
              <a
                href="/marketplace"
                className="w-full px-4 py-3 bg-neon-green text-black rounded-lg font-semibold hover:bg-green-400 transition-colors flex items-center justify-center"
              >
                <Home className="w-4 h-4 mr-2" />
                Browse Creators
              </a>

              <a
                href="/"
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors block text-center"
              >
                Return Home
              </a>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <p className="text-xs text-gray-500 mt-4">
                Error: {this.state.error?.message}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default UsernameErrorBoundary;